import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  useCategoriasCalidad,
  useEliminarFicha,
  useFicha,
  useGuardarFicha,
} from '@/hooks/useCalidad';
import { useSubPartidas } from '@/hooks/useSubPartidas';
import { cn } from '@/lib/utils';
import { VistaPreviaFicha } from '@/pages/campo/RegistroCalidadPantalla';
import type {
  AmbitoFicha,
  FichaDetalle,
  GuardarFicha,
  TipoBloqueo,
  TipoFicha,
  TipoValor,
} from '@adelante/shared';
import {
  ArrowDown,
  ArrowUp,
  CaretLeft,
  CircleNotch,
  DeviceMobile,
  Plus,
  Trash,
  X,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Editor de una ficha de control: encabezado, subpartidas donde aplica,
 * secciones y puntos. Guarda la estructura completa (PUT); los puntos que se
 * quitan quedan inactivos (los registros ya hechos conservan su copia).
 */

type Seccion = GuardarFicha['secciones'][number];
type Punto = Seccion['puntos'][number];

const SELECT =
  'h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60';

const puntoVacio = (): Punto => ({
  texto: '',
  criterio: null,
  obligatorio: false,
  requiere_foto: false,
  tipo_valor: 'ninguno',
  unidad: null,
  valor_min: null,
  valor_max: null,
});

function desdeDetalle(f: FichaDetalle): GuardarFicha {
  return {
    codigo: f.codigo,
    nombre: f.nombre,
    descripcion: f.descripcion,
    categoria: f.categoria,
    tipo: f.tipo,
    ambito: f.ambito,
    bloqueo: f.bloqueo,
    activo: f.activo,
    sub_partida_ids: f.sub_partidas.map((s) => s.sub_partida_id),
    secciones: f.secciones.map((s) => ({
      id: s.id,
      nombre: s.nombre,
      sub_partida_revisada_id: s.sub_partida_revisada_id,
      puntos: s.puntos.map((p) => ({
        id: p.id,
        texto: p.texto,
        criterio: p.criterio,
        obligatorio: p.obligatorio,
        requiere_foto: p.requiere_foto,
        tipo_valor: p.tipo_valor,
        unidad: p.unidad,
        valor_min: p.valor_min,
        valor_max: p.valor_max,
      })),
    })),
  };
}

function mover<T>(arr: T[], i: number, delta: number): T[] {
  const j = i + delta;
  if (j < 0 || j >= arr.length) return arr;
  const copia = [...arr];
  [copia[i], copia[j]] = [copia[j] as T, copia[i] as T];
  return copia;
}

export function FichaEditorPantalla() {
  const { id = 'nueva' } = useParams<{ id: string }>();
  const nueva = id === 'nueva';
  const fichaQ = useFicha(nueva ? null : id);

  if (!nueva && fichaQ.isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando ficha…
      </div>
    );
  }
  if (!nueva && (fichaQ.isError || !fichaQ.data)) {
    return <p className="py-8 text-destructive">No se pudo cargar la ficha.</p>;
  }
  const inicial: GuardarFicha = fichaQ.data
    ? desdeDetalle(fichaQ.data)
    : {
        codigo: '',
        nombre: '',
        descripcion: null,
        categoria: 'calidad_constructiva',
        tipo: 'inspeccion',
        ambito: 'vivienda',
        bloqueo: 'inicio',
        activo: true,
        sub_partida_ids: [],
        secciones: [{ nombre: 'General', sub_partida_revisada_id: null, puntos: [puntoVacio()] }],
      };
  return <Editor key={id} fichaId={nueva ? null : id} inicial={inicial} />;
}

function Editor({ fichaId, inicial }: { fichaId: string | null; inicial: GuardarFicha }) {
  const navigate = useNavigate();
  const categoriasQ = useCategoriasCalidad();
  const subsQ = useSubPartidas();
  const subs = subsQ.data?.data ?? [];
  const guardar = useGuardarFicha();
  const eliminar = useEliminarFicha();
  const [confirmarEliminar, setConfirmarEliminar] = useState(false);
  const [f, setF] = useState<GuardarFicha>(inicial);
  const [subAgregar, setSubAgregar] = useState('');
  const [verPrevia, setVerPrevia] = useState(false);

  const subPorId = useMemo(() => new Map(subs.map((s) => [s.id, s])), [subs]);
  const total = f.secciones.reduce((a, s) => a + s.puntos.length, 0);

  const set = (cambio: Partial<GuardarFicha>) => setF((prev) => ({ ...prev, ...cambio }));
  const setSeccion = (i: number, cambio: Partial<Seccion>) =>
    setF((prev) => ({
      ...prev,
      secciones: prev.secciones.map((s, k) => (k === i ? { ...s, ...cambio } : s)),
    }));
  const setPunto = (i: number, j: number, cambio: Partial<Punto>) =>
    setF((prev) => ({
      ...prev,
      secciones: prev.secciones.map((s, k) =>
        k === i ? { ...s, puntos: s.puntos.map((p, m) => (m === j ? { ...p, ...cambio } : p)) } : s,
      ),
    }));

  async function onGuardar() {
    const vacios = f.secciones.some(
      (s) => !s.nombre.trim() || s.puntos.some((p) => p.texto.trim().length < 2),
    );
    if (!f.codigo.trim() || !f.nombre.trim() || vacios) {
      toast.error('Complete código, nombre, nombres de sección y texto de cada punto');
      return;
    }
    if (f.secciones.some((s) => s.puntos.length === 0)) {
      toast.error('Cada sección debe tener al menos un punto');
      return;
    }
    try {
      const r = await guardar.mutateAsync({ id: fichaId, body: f });
      toast.success('Ficha guardada');
      if (!fichaId) navigate(`/admin/calidad-fichas/${r.id}`, { replace: true });
    } catch (e) {
      toast.error('No se pudo guardar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  async function onEliminar() {
    if (!fichaId) return;
    try {
      await eliminar.mutateAsync(fichaId);
      toast.success(`Ficha ${f.codigo} eliminada`);
      navigate('/admin/calidad-fichas', { replace: true });
    } catch (e) {
      setConfirmarEliminar(false);
      toast.error('No se pudo eliminar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return (
    <div className="space-y-6 pb-12">
      <Link
        to="/admin/calidad-fichas"
        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <CaretLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
        Fichas de control
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {fichaId ? f.nombre || 'Ficha' : 'Nueva ficha'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {f.secciones.length} secciones · {total} puntos ·{' '}
            {f.secciones.reduce((a, s) => a + s.puntos.filter((p) => p.obligatorio).length, 0)}{' '}
            obligatorios
          </p>
        </div>
        <div className="flex gap-2">
          {fichaId && (
            <Button
              variant="ghost"
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setConfirmarEliminar(true)}
            >
              <Trash className="h-4 w-4" weight="bold" aria-hidden="true" />
              Eliminar
            </Button>
          )}
          <Button variant="outline" onClick={() => setVerPrevia(true)}>
            <DeviceMobile className="h-4 w-4" weight="bold" aria-hidden="true" />
            Vista previa
          </Button>
          <Button onClick={onGuardar} disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar ficha'}
          </Button>
        </div>
      </div>

      <Dialog
        open={confirmarEliminar}
        onOpenChange={(o) => !eliminar.isPending && setConfirmarEliminar(o)}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Eliminar ficha {f.codigo}</DialogTitle>
            <DialogDescription>
              Se borra la ficha con sus secciones y puntos. Solo es posible si nunca se ha usado
              (sin inspecciones ni evaluaciones registradas); si ya tiene registros, desactívela.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmarEliminar(false)}
              disabled={eliminar.isPending}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={onEliminar} disabled={eliminar.isPending}>
              {eliminar.isPending ? 'Eliminando…' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={verPrevia} onOpenChange={setVerPrevia}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Vista previa en campo</DialogTitle>
            <DialogDescription>
              Así verá la ficha el usuario en el teléfono (incluye cambios sin guardar). Puede tocar
              las respuestas para probar; nada se guarda.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border-4 border-zinc-800 bg-zinc-50 p-3">
            <VistaPreviaFicha ficha={f} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Encabezado */}
      <Card>
        <CardHeader>
          <CardTitle>Datos generales</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Código</Label>
            <Input
              value={f.codigo}
              onChange={(e) => set({ codigo: e.target.value })}
              placeholder="INSP-1.1.4"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Nombre</Label>
            <Input value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <select
              className={SELECT}
              value={f.tipo}
              disabled={!!fichaId}
              onChange={(e) => set({ tipo: e.target.value as TipoFicha })}
              title={fichaId ? 'El tipo no se cambia en una ficha existente' : undefined}
            >
              <option value="inspeccion">Inspección (antes · bloquea)</option>
              <option value="evaluacion">Evaluación (después · NC)</option>
            </select>
          </div>
          {f.tipo === 'inspeccion' && (
            <div className="space-y-1.5">
              <Label>Bloquea (mientras no esté aprobada)</Label>
              <select
                className={SELECT}
                value={f.bloqueo ?? 'inicio'}
                onChange={(e) => set({ bloqueo: e.target.value as TipoBloqueo })}
              >
                <option value="inicio">Al iniciar · no se registra avance</option>
                <option value="completar">Al completar · no se marca 100%</option>
                <option value="ninguno">No bloquea · solo registra</option>
              </select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Ámbito</Label>
            <select
              className={SELECT}
              value={f.ambito}
              onChange={(e) => set({ ambito: e.target.value as AmbitoFicha })}
            >
              <option value="vivienda">Vivienda (sub-partidas)</option>
              <option value="centro_costo">Centro de costo</option>
              <option value="proceso">Proceso</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Categoría</Label>
            <select
              className={SELECT}
              value={f.categoria}
              onChange={(e) => set({ categoria: e.target.value })}
            >
              {(categoriasQ.data ?? []).map((c) => (
                <option key={c.codigo} value={c.codigo}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Descripción</Label>
            <Input
              value={f.descripcion ?? ''}
              onChange={(e) => set({ descripcion: e.target.value || null })}
            />
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch
              checked={f.activo ?? true}
              onCheckedChange={(v) => set({ activo: v })}
              id="activo"
            />
            <Label htmlFor="activo">Activa</Label>
          </div>

          {f.ambito === 'vivienda' && (
            <div className="space-y-1.5 md:col-span-3">
              <Label>Aplica a las sub-partidas</Label>
              <div className="flex flex-wrap items-center gap-1.5">
                {(f.sub_partida_ids ?? []).map((sid) => {
                  const s = subPorId.get(sid);
                  return (
                    <span
                      key={sid}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-zinc-50 px-2 py-0.5 text-xs"
                    >
                      {s ? `${s.codigo} ${s.nombre}` : sid}
                      <button
                        type="button"
                        onClick={() =>
                          set({ sub_partida_ids: f.sub_partida_ids?.filter((x) => x !== sid) })
                        }
                        aria-label="Quitar"
                      >
                        <X className="h-3 w-3" weight="bold" />
                      </button>
                    </span>
                  );
                })}
                <select
                  className={cn(SELECT, 'h-8 w-64')}
                  value={subAgregar}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v && !f.sub_partida_ids?.includes(v))
                      set({ sub_partida_ids: [...(f.sub_partida_ids ?? []), v] });
                    setSubAgregar('');
                  }}
                >
                  <option value="">+ Agregar sub-partida…</option>
                  {subs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.codigo} {s.nombre}
                    </option>
                  ))}
                </select>
              </div>
              {f.tipo === 'inspeccion' && (
                <p className="text-xs text-muted-foreground">
                  {f.bloqueo === 'completar'
                    ? 'Estas sub-partidas no se podrán marcar al 100% sin la inspección aprobada.'
                    : f.bloqueo === 'ninguno'
                      ? 'La inspección no bloquea el avance de estas sub-partidas.'
                      : 'Estas sub-partidas no se podrán iniciar sin la inspección aprobada.'}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Secciones */}
      {f.secciones.map((sec, i) => (
        <Card key={sec.id ?? `nueva-${i}`}>
          <CardHeader className="flex flex-row flex-wrap items-center gap-2 space-y-0">
            <Input
              value={sec.nombre}
              onChange={(e) => setSeccion(i, { nombre: e.target.value })}
              className="h-9 max-w-xs font-semibold"
              placeholder="Nombre de la sección"
            />
            {f.ambito === 'vivienda' && (
              <select
                className={cn(SELECT, 'w-64')}
                value={sec.sub_partida_revisada_id ?? ''}
                onChange={(e) =>
                  setSeccion(i, {
                    sub_partida_revisada_id: e.target.value ? Number(e.target.value) : null,
                  })
                }
                title="Sub-partida (oficio) que revisa esta sección"
              >
                <option value="">Sub-partida revisada: —</option>
                {subs.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.codigo} {s.nombre}
                  </option>
                ))}
              </select>
            )}
            <div className="ml-auto flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => set({ secciones: mover(f.secciones, i, -1) })}
                aria-label="Subir sección"
              >
                <ArrowUp className="h-4 w-4" weight="bold" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => set({ secciones: mover(f.secciones, i, 1) })}
                aria-label="Bajar sección"
              >
                <ArrowDown className="h-4 w-4" weight="bold" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                disabled={f.secciones.length === 1}
                onClick={() => set({ secciones: f.secciones.filter((_, k) => k !== i) })}
                aria-label="Quitar sección"
              >
                <Trash className="h-4 w-4 text-red-600" weight="bold" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {sec.puntos.map((p, j) => (
              <div
                key={p.id ?? `p-${j}`}
                className="grid items-center gap-2 rounded-md border border-border p-2 md:grid-cols-[1fr_auto]"
              >
                <div className="grid gap-2 md:grid-cols-2">
                  <Input
                    value={p.texto}
                    onChange={(e) => setPunto(i, j, { texto: e.target.value })}
                    placeholder="Punto de control"
                    className="font-medium"
                  />
                  <Input
                    value={p.criterio ?? ''}
                    onChange={(e) => setPunto(i, j, { criterio: e.target.value || null })}
                    placeholder="Criterio de aceptación (opcional)"
                  />
                  <div className="flex flex-wrap items-center gap-3 md:col-span-2">
                    <label htmlFor={`ob-${i}-${j}`} className="flex items-center gap-1.5 text-xs">
                      <Checkbox
                        id={`ob-${i}-${j}`}
                        checked={p.obligatorio}
                        onCheckedChange={(v) => setPunto(i, j, { obligatorio: v === true })}
                      />
                      Obligatorio
                    </label>
                    <label htmlFor={`foto-${i}-${j}`} className="flex items-center gap-1.5 text-xs">
                      <Checkbox
                        id={`foto-${i}-${j}`}
                        checked={p.requiere_foto}
                        onCheckedChange={(v) => setPunto(i, j, { requiere_foto: v === true })}
                      />
                      Requiere foto
                    </label>
                    <select
                      className={cn(SELECT, 'h-8 w-36 text-xs')}
                      value={p.tipo_valor}
                      onChange={(e) => setPunto(i, j, { tipo_valor: e.target.value as TipoValor })}
                    >
                      <option value="ninguno">Sin valor</option>
                      <option value="valor">Valor medido</option>
                      <option value="calificacion">Calificación 1–5</option>
                    </select>
                    {p.tipo_valor === 'valor' && (
                      <>
                        <Input
                          value={p.unidad ?? ''}
                          onChange={(e) => setPunto(i, j, { unidad: e.target.value || null })}
                          placeholder="Unidad"
                          className="h-8 w-20 text-xs"
                        />
                        <Input
                          type="number"
                          value={p.valor_min ?? ''}
                          onChange={(e) =>
                            setPunto(i, j, {
                              valor_min: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          placeholder="Mín"
                          className="h-8 w-20 text-xs"
                        />
                        <Input
                          type="number"
                          value={p.valor_max ?? ''}
                          onChange={(e) =>
                            setPunto(i, j, {
                              valor_max: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          placeholder="Máx"
                          className="h-8 w-20 text-xs"
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 md:flex-col">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setSeccion(i, { puntos: mover(sec.puntos, j, -1) })}
                    aria-label="Subir punto"
                  >
                    <ArrowUp className="h-3.5 w-3.5" weight="bold" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setSeccion(i, { puntos: mover(sec.puntos, j, 1) })}
                    aria-label="Bajar punto"
                  >
                    <ArrowDown className="h-3.5 w-3.5" weight="bold" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setSeccion(i, { puntos: sec.puntos.filter((_, m) => m !== j) })}
                    aria-label="Quitar punto"
                  >
                    <Trash className="h-3.5 w-3.5 text-red-600" weight="bold" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSeccion(i, { puntos: [...sec.puntos, puntoVacio()] })}
            >
              <Plus className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              Agregar punto
            </Button>
          </CardContent>
        </Card>
      ))}

      <Button
        variant="outline"
        onClick={() =>
          set({
            secciones: [
              ...f.secciones,
              { nombre: '', sub_partida_revisada_id: null, puntos: [puntoVacio()] },
            ],
          })
        }
      >
        <Plus className="h-4 w-4" weight="bold" aria-hidden="true" />
        Agregar sección
      </Button>
    </div>
  );
}
